import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { FaEdit, FaEye, FaTrashAlt, FaSort } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import styled from "styled-components";
import debounce from "lodash.debounce";
import axios from "axios";
import { toast } from "react-toastify";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const ListRequest = () => {
  // Styled Components for CSS-in-JS
  const TableContainer = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  `;

  const StatusBadge = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    ${({ status }) =>
      status === "approve"
        ? "background: #dcfce7; color: #16a34a;"
        : status === "reject"
        ? "background: #fee2e2; color: #dc2626;"
        : status === "pending"
        ? "background: #fef9c3; color: #ca8a04;"
        : status === "inprogress"
        ? "background: #dbeafe; color: #2563eb;"
        : "background: #f3f4f6; color: #4b5563;"}
  `;

  const navigate = useNavigate();
  const [request, setRequest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [filters, setFilters] = useState({
    search: "",
    startDate: null,
    endDate: null,
  });
  const [pagination, setPagination] = useState({
    pageSize: 10,
    currentPage: 1,
  });

  const fetchData = useCallback(async () => {
    try {
      const meResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        { withCredentials: true }
      );

      if (!meResponse.data?.success) {
        navigate("/user/login");
        return;
      }

      const requestsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/request`
      );

      setRequest(requestsResponse.data || []);
      console.log("requests fetched successfully:", requestsResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/user/login");
      } else {
        toast.error("Error!", error.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sorting functionality
  const sortedRequests = useMemo(() => {
    return [...request].sort((a, b) => {
      if (!sortConfig.field) return 0;
      if (a[sortConfig.field] < b[sortConfig.field])
        return sortConfig.direction === "asc" ? -1 : 1;
      if (a[sortConfig.field] > b[sortConfig.field])
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [request, sortConfig]);

  // Filtering functionality with debounced search
  const filteredRequests = useMemo(() => {
    return sortedRequests.filter((item) => {
      const matchesSearch =
        item.request_name
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        item.category_name
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        item.status?.toLowerCase().includes(filters.search.toLowerCase());

      const withinDateRange =
        (!filters.startDate ||
          new Date(item.created_at) >= filters.startDate) &&
        (!filters.endDate || new Date(item.created_at) <= filters.endDate);

      return matchesSearch && withinDateRange;
    });
  }, [sortedRequests, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / pagination.pageSize);
  const currentData = useMemo(() => {
    return filteredRequests.slice(
      (pagination.currentPage - 1) * pagination.pageSize,
      pagination.currentPage * pagination.pageSize
    );
  }, [filteredRequests, pagination]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  // Handle filter changes with debouncing
  const handleFilterChange = useCallback(
    debounce((e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    }, 300),
    []
  );

  // Handle date changes
  const handleDateChange = useCallback((date, field) => {
    setFilters((prev) => ({ ...prev, [field]: date }));
  }, []);

  // Handle page size changes
  const handlePageSizeChange = useCallback((e) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: Number(e.target.value),
      currentPage: 1,
    }));
  }, []);

  // Handle viewing a request
  const handleViewRequest = useCallback(
    (id) => {
      navigate(`/user/view_request/${id}`);
    },
    [navigate]
  );

  // Handle updating a request
  const handleUpdateRequest = useCallback(
    (id) => {
      navigate(`/user/update_request/${id}`);
    },
    [navigate]
  );

  // Handle deleting a request
  const handleDelete = useCallback(async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to delete this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/request/${id}`,
          { withCredentials: true }
        );

        if (response.data.success) {
          toast.success("Deleted!", "Your request has been deleted.");
          setRequest((prevRequests) =>
            prevRequests.filter((item) => item.id !== id)
          );
        } else {
          toast.error("Error!", "Failed to delete the request.", "error");
        }
      } catch (error) {
        toast.fire("Error!", "Something went wrong.", "error");
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-transparent">
        <ClipLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  return (
    <div className="p-1 lg:p-4 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800"> Requests</h1>
        <button
          onClick={() => navigate("/user/create_request")}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <span>+ New request</span>
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-1 lg:p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          {/* Search Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search requests
            </label>
            <input
              type="text"
              name="search"
              placeholder="Search requests..."
              className="w-60 px-3 py-2 border rounded-md focus:ring-1 focus:outline-none focus:ring-indigo-200 focus:border-indigo-200"
              onChange={handleFilterChange}
            />
          </div>

          {/* Date Filter */}
          <div className="flex space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                onChange={(e) =>
                  handleDateChange(new Date(e.target.value), "startDate")
                }
                className="w-40 px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                onChange={(e) =>
                  handleDateChange(new Date(e.target.value), "endDate")
                }
                className="w-40 px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <TableContainer>
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Show:</span>
            <select
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
            >
              {[10, 20, 30, 50].map((size) => (
                <option key={size} value={size}>
                  {size} entries
                </option>
              ))}
            </select>
          </div>
          <span className="text-sm text-gray-600">
            Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              filteredRequests.length
            )}{" "}
            of {filteredRequests.length} entries
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {["ID", "Request Name", "Type", "Status", "Actions"].map(
                  (header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() =>
                        header !== "Actions" &&
                        handleSort(header.toLowerCase().replace(" ", "_"))
                      }
                    >
                      <div className="flex items-center gap-2">
                        {header}
                        {sortConfig.field ===
                          header.toLowerCase().replace(" ", "_") && (
                          <FaSort
                            className={`transform ${
                              sortConfig.direction === "desc"
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {currentData.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {(pagination.currentPage - 1) * pagination.pageSize +
                      index +
                      1}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {item.request_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.category_name}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status}>
                      {item.status}
                    </StatusBadge>
                  </td>

                  <td className="px-4 py-3 flex gap-2">
                    <button
                      onClick={() => handleViewRequest(item.id)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <FaEye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleUpdateRequest(item.id)}
                      className="p-2 rounded-lg text-yellow-600 hover:bg-yellow-50"
                    >
                      <FaEdit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                    >
                      <FaTrashAlt className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {/* Pagination */}
               <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                 <div className="flex-1 flex justify-between sm:hidden">
                   <button
                     onClick={() => setPagination((prev) => Math.max(prev - 1, 1))}
                     disabled={pagination.currentPage === 1}
                     className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
                   >
                     Previous
                   </button>
                   <button
                     onClick={() =>
                       setPagination((prev) => Math.min(prev + 1, totalPages))
                     }
                     disabled={pagination.currentPage === totalPages || totalPages === 0}
                     className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
                   >
                     Next
                   </button>
                 </div>
                 <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                   <div>
                     <p className="text-sm text-gray-700">
                       Showing{" "}
                       <span className="font-medium">{(pagination.currentPage - 1) * pagination.pageSize + 1}</span> to{" "}
                       <span className="font-medium">
                          {Math.min(
                    pagination.currentPage * pagination.pageSize,
                    filteredRequests.length
                  )}
                       </span>{" "}
                       of{" "}
                       <span className="font-medium">{filteredRequests.length}</span>{" "}
                       results
                     </p>
                   </div>
                   <div>
                     <nav
                       className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                       aria-label="Pagination"
                     >
                       <button
                         onClick={() => setPagination((prev) => ({ ...prev, currentPage: 1 }))}
                         disabled={pagination.currentPage === 1}
                         className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                       >
                         <span className="sr-only">First</span>
                         <FiChevronLeft className="h-5 w-5" />
                         <FiChevronLeft className="h-5 w-5 -ml-3" />
                       </button>
                       <button
                         onClick={() =>
                           setPagination((prev) => ({ ...prev, currentPage: Math.max(prev.currentPage - 1, 1) }))
                         }
                         disabled={pagination.currentPage === 1}
                         className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                       >
                         <span className="sr-only">Previous</span>
                         <FiChevronLeft className="h-5 w-5" />
                       </button>
                       {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                         let pageNum;
                         if (totalPages <= 5) {
                           pageNum = i + 1;
                         } else if (pagination.currentPage <= 3) {
                           pageNum = i + 1;
                         } else if (pagination.currentPage >= totalPages - 2) {
                           pageNum = totalPages - 4 + i;
                         } else {
                           pageNum = pagination.currentPage - 2 + i;
                         }
                         return (
                           <button
                             key={pageNum}
                             onClick={() => setPagination(pageNum)}
                             className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                               pagination.currentPage === pageNum
                                 ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                 : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                             }`}
                           >
                             {pageNum}
                           </button>
                         );
                       })}
                       <button
                         onClick={() =>
                           setPagination((prev) => ({ ...prev, currentPage: Math.min(prev.currentPage + 1, totalPages) }))
                         }
                         disabled={pagination.currentPage === totalPages || totalPages === 0}
                         className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                       >
                         <span className="sr-only">Next</span>
                         <FiChevronRight className="h-5 w-5" />
                       </button>
                       <button
                         onClick={() => setPagination((prev) => ({ ...prev, currentPage: totalPages }))}
                         disabled={pagination.currentPage === totalPages || totalPages === 0}
                         className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                       >
                         <span className="sr-only">Last</span>
                         <FiChevronRight className="h-5 w-5" />
                         <FiChevronRight className="h-5 w-5 -ml-3" />
                       </button>
                     </nav>
                   </div>
                 </div>
               </div>
      </TableContainer>
    </div>
  );
};

export default ListRequest;